import { useState, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../hooks/useAuth'
import { AppLauncher } from '../components/AppLauncher'

type MfaStatus = 'idle' | 'loading' | 'confirming' | 'success' | 'error'
type MfaData   = { secretCode: string; otpUri: string }

export function ProfilePage() {
  const { user, logout }      = useAuthStore()
  const navigate              = useNavigate()
  const [mfaStatus, setMfaStatus] = useState<MfaStatus>('idle')
  const [mfaData, setMfaData]     = useState<MfaData | null>(null)
  const [mfaCode, setMfaCode]     = useState('')
  const [mfaError, setMfaError]   = useState('')

  const handleLogout = async () => { await logout(); navigate('/login') }

  // ─── MFA Enrollment ────────────────────────────────────────────────────────

  const startMfaEnroll = async () => {
    setMfaStatus('loading')
    setMfaError('')
    try {
      const res = await fetch('/api/auth/mfa-enroll', { credentials: 'include' })
      if (!res.ok) throw new Error('Failed to start enrollment')
      setMfaData(await res.json())
      setMfaStatus('confirming')
    } catch {
      setMfaError('Failed to start enrollment. Make sure you are signed in.')
      setMfaStatus('error')
    }
  }

  const confirmMfaEnroll = async (e: FormEvent) => {
    e.preventDefault()
    setMfaError('')
    try {
      const res = await fetch('/api/auth/mfa-confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: mfaCode.trim() }),
      })
      if (!res.ok) {
        const d = await res.json().catch(() => ({}))
        setMfaError(d.message || 'Invalid code — check your authenticator and try again')
        return
      }
      setMfaStatus('success')
    } catch {
      setMfaError('Verification failed. Please try again.')
    }
  }

  const cancelMfaEnroll = () => { setMfaStatus('idle'); setMfaData(null); setMfaCode(''); setMfaError('') }

  return (
    <div className="auth-card max-w-lg">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Profile</h1>
        <p className="text-gray-600">Manage your SCJ account</p>
      </div>

      <div className="space-y-6">

        {/* Account Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Account Information</h2>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Email:</span>
              <span className="font-medium">{user?.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span className="font-medium text-green-600">
                {user?.email_verified ? 'Verified' : 'Unverified'}
              </span>
            </div>
          </div>
        </div>

        {/* MFA Enrollment */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Two-Factor Authentication</h2>

          {mfaStatus === 'idle' && (
            <div>
              <p className="text-sm text-gray-600 mb-3">
                Add an authenticator app (Google Authenticator, Authy, 1Password) for extra security.
              </p>
              <button
                onClick={startMfaEnroll}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
              >
                Set Up Authenticator App
              </button>
            </div>
          )}

          {mfaStatus === 'loading' && (
            <p className="text-sm text-gray-500">Setting up MFA…</p>
          )}

          {mfaStatus === 'error' && (
            <div>
              <p className="text-sm text-red-600 mb-2">{mfaError}</p>
              <button onClick={() => setMfaStatus('idle')} className="text-sm auth-link">Try again</button>
            </div>
          )}

          {mfaStatus === 'confirming' && mfaData && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Open your authenticator app and add a new account manually using the key below,
                or tap the link on your mobile device.
              </p>

              <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Secret Key</p>
                <p className="font-mono text-sm break-all select-all">{mfaData.secretCode}</p>
                <a
                  href={mfaData.otpUri}
                  className="block text-sm auth-link"
                >
                  Open in authenticator app →
                </a>
              </div>

              {mfaError && <p className="text-sm text-red-600">{mfaError}</p>}

              <form onSubmit={confirmMfaEnroll} className="space-y-3">
                <div>
                  <label htmlFor="mfaConfirmCode" className="block text-sm font-medium text-gray-700 mb-1">
                    Verification Code
                  </label>
                  <input
                    id="mfaConfirmCode"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={mfaCode}
                    onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                    className="auth-input text-center text-2xl tracking-widest"
                    placeholder="000000"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={mfaCode.length !== 6}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Verify and Enable MFA
                </button>
              </form>
              <button type="button" onClick={cancelMfaEnroll} className="text-sm auth-link">
                Cancel
              </button>
            </div>
          )}

          {mfaStatus === 'success' && (
            <div className="flex items-center space-x-2 text-green-600">
              <span className="text-lg">✓</span>
              <p className="text-sm font-medium">MFA enabled — your account is now protected.</p>
            </div>
          )}
        </div>

        {/* Connected Services — registry in config/apps.ts */}
        <AppLauncher groups={user?.groups ?? []} isAdmin={user?.isAdmin ?? false} />

        {/* Admin */}
        {user?.isAdmin && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Administration</h2>
            <button
              onClick={() => navigate('/settings')}
              className="w-full bg-gray-800 hover:bg-gray-900 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
            >
              User Management →
            </button>
          </div>
        )}

        <div className="pt-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  )
}
